/**
 * React Query hook for user tasks
 * Provides access to personal tasks with filtering and time windows
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveTeamId } from '@/contexts/TeamContext';
import {
  listTasks,
  listAllTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  restoreTask,
  toggleTaskDone,
  snoozeTask,
  rescheduleTask,
  bulkUpdateTaskStatus,
  bulkDeleteTasks,
} from '@/services/notepad/tasks.service';
import type { TaskCreatedVia } from '@/features/tasks/task.mapper';
import { dashboardRingsKeys } from '@/hooks/useDashboardRings';
import { logger } from '@/lib/logger';
import { Task, TaskInput, TaskQueryOptions, TaskStatus } from '@/types/notepad';
import { notepadToast } from '@/lib/toast/notepadToast';

/**
 * Query keys factory for tasks
 */
export const taskKeys = {
  all: (userId: string) => ['tasks', userId] as const,
  lists: (userId: string) => [...taskKeys.all(userId), 'list'] as const,
  list: (userId: string, options?: TaskQueryOptions) =>
    [...taskKeys.lists(userId), options] as const,
  details: (userId: string) => [...taskKeys.all(userId), 'detail'] as const,
  detail: (userId: string, taskId: string) => [...taskKeys.details(userId), taskId] as const,
};

/**
 * Hook to list user's personal tasks with optional filters
 * Auto-cancels when user logs out
 */
export function useMyTasks(options: TaskQueryOptions = {}) {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: taskKeys.list(userId || '', options),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return listTasks(userId, options);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to list ALL tasks (personal + team) merged together
 * Each task has correct scope set for subsequent mutations
 * Use this for the drawer task list to show both personal and team tasks
 */
export function useAllTasks(options: TaskQueryOptions = {}) {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: ['allTasks', userId, teamId, options] as const,
    queryFn: async () => {
      if (!userId || !teamId) {
        throw new Error('User or team not authenticated');
      }
      return listAllTasks(userId, teamId, options);
    },
    enabled: !!userId && !!teamId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to get a single task
 * Checks both personal tasks and team tasks
 */
export function useTask(taskId: string) {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: taskKeys.detail(userId || '', taskId),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return getTask(userId, taskId, teamId || undefined);
    },
    enabled: !!userId && !!taskId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to create a new task
 */
export function useCreateTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: TaskInput & { createdVia?: TaskCreatedVia; createdViaRefId?: string }
    ) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return createTask(userId, input, {
        mapperOptions: input.createdVia
          ? { createdVia: input.createdVia, createdViaRefId: input.createdViaRefId }
          : undefined,
      });
    },
    onSuccess: (newTask) => {
      // Invalidate all task lists
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      // Optimistically add to cache
      queryClient.setQueryData<Task[]>(taskKeys.list(userId || '', {}), (old) =>
        old ? [newTask, ...old] : [newTask]
      );

      notepadToast.taskCreated();
    },
    onError: (error: Error) => {
      logger.error('[useCreateTask] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to update a task
 * Routes to correct Firestore path based on task scope
 */
export function useUpdateTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, input }: { taskId: string; input: Partial<TaskInput> }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      // Pass teamId so updateTask can route to correct Firestore path
      return updateTask(userId, taskId, input, teamId || undefined);
    },
    onSuccess: (updatedTask) => {
      // Invalidate lists and detail with refetch
      void queryClient.invalidateQueries({
        queryKey: taskKeys.lists(userId || ''),
        refetchType: 'active',
      });
      queryClient.setQueryData(taskKeys.detail(userId || '', updatedTask.id), updatedTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useUpdateTask] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to toggle task done status
 * Handles both personal tasks and team tasks
 */
export function useToggleTaskDone() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      // Pass teamId so toggleTaskDone can handle team tasks
      return toggleTaskDone(userId, taskId, teamId || undefined);
    },
    onMutate: async (taskId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.lists(userId || '') });

      // Snapshot previous value
      const previousTasks = queryClient.getQueriesData({ queryKey: taskKeys.lists(userId || '') });

      // Optimistically update all task lists
      queryClient.setQueriesData(
        { queryKey: taskKeys.lists(userId || '') },
        (old: Task[] | undefined) => {
          if (!old || !Array.isArray(old)) return old;

          return old.map((task: Task) => {
            if (task.id === taskId) {
              const newStatus: TaskStatus = task.status === 'done' ? 'open' : 'done';
              return {
                ...task,
                status: newStatus,
                completedAt: newStatus === 'done' ? new Date() : null,
              };
            }
            return task;
          });
        }
      );

      return { previousTasks };
    },
    onSuccess: (updatedTask) => {
      // Update with server data and force refetch
      void queryClient.invalidateQueries({
        queryKey: taskKeys.lists(userId || ''),
        refetchType: 'active',
      });
      queryClient.setQueryData(taskKeys.detail(userId || '', updatedTask.id), updatedTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error, _taskId, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        context.previousTasks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      logger.error('[useToggleTaskDone] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to snooze a task
 */
export function useSnoozeTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, minutes }: { taskId: string; minutes: number }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return snoozeTask(userId, taskId, minutes);
    },
    onSuccess: (updatedTask) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });
      queryClient.setQueryData(taskKeys.detail(userId || '', updatedTask.id), updatedTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useSnoozeTask] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to reschedule a task
 */
export function useRescheduleTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, dueAt }: { taskId: string; dueAt: Date }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return rescheduleTask(userId, taskId, dueAt);
    },
    onSuccess: (updatedTask) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });
      queryClient.setQueryData(taskKeys.detail(userId || '', updatedTask.id), updatedTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useRescheduleTask] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to set reminder for a task
 */
export function useSetTaskReminder() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, remindAt }: { taskId: string; remindAt: Date | null }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return updateTask(userId, taskId, { remindAt });
    },
    onSuccess: (updatedTask) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });
      queryClient.setQueryData(taskKeys.detail(userId || '', updatedTask.id), updatedTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      if (updatedTask.remindAt) {
        notepadToast.reminderSet();
      } else {
        notepadToast.reminderRemoved();
      }
    },
    onError: (error: Error) => {
      logger.error('[useSetTaskReminder] error:', error);
      notepadToast.reminderError(error);
    },
  });
}

/**
 * Hook to delete a task
 */
export function useDeleteTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return deleteTask(userId, taskId);
    },
    onMutate: async (taskId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.lists(userId || '') });

      // Snapshot previous value
      const previousTasks = queryClient.getQueriesData({ queryKey: taskKeys.lists(userId || '') });

      // Optimistically remove task from all lists
      queryClient.setQueriesData<Task[]>(
        { queryKey: taskKeys.lists(userId || '') },
        (old) => old?.filter((task) => task.id !== taskId) ?? []
      );

      return { previousTasks };
    },
    onError: (error: Error, _taskId, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        context.previousTasks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('[useDeleteTask] error:', error);
      notepadToast.taskError(error);
    },
    onSettled: () => {
      // Refetch after mutation
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }
    },
  });
}

/**
 * Hook to restore a deleted task
 */
export function useRestoreTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return restoreTask(userId, taskId);
    },
    onSuccess: (restoredTask) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });
      queryClient.setQueryData(taskKeys.detail(userId || '', restoredTask.id), restoredTask);

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useRestoreTask] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to bulk update task statuses
 */
export function useBulkUpdateTaskStatus() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskIds, status }: { taskIds: string[]; status: TaskStatus }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return bulkUpdateTaskStatus(userId, taskIds, status);
    },
    onSuccess: (_) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useBulkUpdateTaskStatus] error:', error);
      notepadToast.taskError(error);
    },
  });
}

/**
 * Hook to bulk delete tasks
 */
export function useBulkDeleteTasks() {
  const { user } = useAuth();
  const userId = user?.uid;
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskIds: string[]) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return bulkDeleteTasks(userId, taskIds);
    },
    onSuccess: (_) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists(userId || '') });

      // Invalidate dashboard rings so Vandaag updates immediately
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: dashboardRingsKeys.byTeam(teamId) });
      }

      notepadToast.taskUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useBulkDeleteTasks] error:', error);
      notepadToast.taskError(error);
    },
  });
}
